import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { usePermission } from "../../hooks/usePermission";
import { apiCall } from "../../_services/apiCall";
import config from "../../config/config";
import { formatKey, getLatestFY } from "../DataManagment/Quantative/component/answerHistoryPage/components/utils";
import TagFilterBar from "./Components/TagFilterBar";
import { parseTags, questionMatchesSteps, getNextEligibleFilters, formatTagKey } from "./Components/TagFilterUtils";
import Spinner from "../../globalComponents/ui/Spinner";
import Checkbox from "../../globalComponents/ui/Checkbox";
import SimpleDropdown from "../../globalComponents/form/dropdowns/SimpleDropdown";
import ViewOnlyBanner from "./Components/ViewOnlyBanner";

function buildTagsPayload(activeSteps) {
  if (!activeSteps || activeSteps.length === 0) return {};
  const result = {};
  const topKey = activeSteps[0].key;
  result[topKey] = activeSteps.map(step => ({
    key: step.key,
    value: Array.from(step.values),
  }));
  return result;
}

function mergeTagsPayload(existingTags, newTags) {
  const merged = JSON.parse(JSON.stringify(existingTags || {}));
  for (const [topKey, newChain] of Object.entries(newTags)) {
    if (!merged[topKey]) {
      merged[topKey] = newChain;
    } else {
      const existingChain = merged[topKey];
      for (const newTagObj of newChain) {
        const idx = existingChain.findIndex(t => t.key === newTagObj.key);
        if (idx !== -1) {
          const existingVals = new Set(existingChain[idx].value);
          newTagObj.value.forEach(v => existingVals.add(v));
          existingChain[idx].value = Array.from(existingVals);
        } else {
          existingChain.push(newTagObj);
        }
      }
    }
  }
  return merged;
}

// ─── pruneTagsByPendingIds ────────────────────────────────────────────────────
// Removes any tag value where no KPI in pendingIds carries that value.
// Drops empty chain entries and empty top-level groups entirely.
function pruneTagsByPendingIds(tags, allQuestions, pendingIds) {
  if (!tags || !Object.keys(tags).length) return {};
  const result = {};
  for (const [topKey, chain] of Object.entries(tags)) {
    const prunedChain = [];
    for (const tagObj of chain) {
      const rawVal = tagObj.value;
      const valuesArray = Array.isArray(rawVal) ? rawVal : (rawVal ? [rawVal] : []);
      const keptValues = valuesArray.filter(val =>
        allQuestions.some(q => {
          if (!pendingIds.has(q.questionId)) return false;
          const combos = parseTags(q.tags);
          return combos.some(combo =>
            combo.some(t => t?.key === tagObj.key && t?.value === val)
          );
        })
      );
      if (keptValues.length > 0) prunedChain.push({ ...tagObj, value: keptValues });
    }
    if (prunedChain.length > 0) result[topKey] = prunedChain;
  }
  return result;
}






const TAG_PALETTES = [
  { bg: "#E6F1FB", border: "#B5D4F4", text: "#0C447C" },
  { bg: "#E1F5EE", border: "#9FE1CB", text: "#085041" },
  { bg: "#FAEEDA", border: "#FAC775", text: "#633806" },
  { bg: "#EEEDFE", border: "#AFA9EC", text: "#3C3489" },
  { bg: "#FAECE7", border: "#F5C4B3", text: "#712B13" },
];

function SelectedTagsDisplay({ existingTags }) {
  const topKeys = Object.keys(existingTags || {});
  if (!topKeys.length) return null;

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: 8,
      }}>
        <span style={{
          fontSize: 10, fontWeight: 700, color: "#94a3b8",
          textTransform: "uppercase", letterSpacing: "0.5px",
        }}>
          Saved tag filters
        </span>
        <span style={{
          fontSize: 11, color: "#94a3b8",
          background: "#f1f5f9", border: "0.5px solid #e2e8f0",
          borderRadius: 6, padding: "1px 8px",
        }}>
          {topKeys.length} group{topKeys.length !== 1 ? "s" : ""}
        </span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {topKeys.map((topKey, groupIdx) => {
          const pal = TAG_PALETTES[groupIdx % TAG_PALETTES.length];
          const chain = existingTags[topKey] || [];

          // Merge all values per unique key across the whole chain
          // e.g. two chain entries with key="category" → one pill "category: A, B"
          const keyValMap = new Map();
          for (const tagObj of chain) {
            const k = tagObj.key;
            if (!keyValMap.has(k)) keyValMap.set(k, []);
            for (const v of (tagObj.value || [])) {
              if (!keyValMap.get(k).includes(v)) keyValMap.get(k).push(v);
            }
          }

          // Build pill list: top key first (no label prefix), then sub-keys with "Key: " prefix
          const pills = [];
          // Top-level key pill (values directly under this key)
          if (keyValMap.has(topKey) && keyValMap.get(topKey).length) {
            pills.push({ label: null, values: keyValMap.get(topKey) });
          }
          // Sub-key pills
          for (const [k, vals] of keyValMap.entries()) {
            if (k === topKey || !vals.length) continue;
            pills.push({ label: formatTagKey(k), values: vals });
          }

          return (
            <div key={topKey} style={{
              display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6,
              padding: "8px 12px",
              background: "#fff",
              border: "0.5px solid #e2e8f0",
              borderRadius: 10,
            }}>
              {/* Group label */}
              <span style={{
                fontSize: 12, fontWeight: 600, color: "#334155",
                whiteSpace: "nowrap",
              }}>
                {formatTagKey(topKey)}
              </span>

              {/* Vertical divider */}
              <span style={{
                width: 1, height: 16, background: "#e2e8f0",
                flexShrink: 0,
              }} />

              {/* One label and individual pills per value, wrapping cleanly */}
              {pills.map(({ label, values }, i) => (
                <div key={i} style={{ display: "inline-flex", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
                  {label && (
                    <span style={{
                      fontWeight: 500,
                      color: "#64748b",
                      fontSize: 11,
                      marginRight: 2
                    }}>
                      {label}:
                    </span>
                  )}
                  {values.map((val, valIdx) => (
                    <span key={valIdx} style={{
                      display: "inline-flex", alignItems: "center",
                      padding: "3px 10px",
                      borderRadius: 20,
                      fontSize: 12,
                      background: pal.bg,
                      border: `0.5px solid ${pal.border}`,
                      color: pal.text,
                      whiteSpace: "nowrap",
                    }}>
                      {val}
                    </span>
                  ))}
                </div>
              ))}

              {pills.length === 0 && (
                <span style={{ fontSize: 11, color: "#cbd5e1" }}>—</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── KPI List Row ─────────────────────────────────────────────────────────────
function KpiListRow({ kpi, checked, onChange, isLast, disabled }) {
  const combos = parseTags(kpi.tags);
  const allTags = []; const seen = new Set();
  for (const combo of combos)
    for (const t of combo) {
      if (!t?.key || !t?.value) continue;
      const k = `${t.key}:::${t.value}`;
      if (!seen.has(k)) { seen.add(k); allTags.push(t); }
    }
  return (
    <div
      onClick={() => { if (!disabled) onChange(!checked); }}
      style={{
        display: "flex", alignItems: "center", gap: 12, padding: "10px 16px",
        borderBottom: isLast ? "none" : "1px solid #f1f5f9",
        cursor: disabled ? "default" : "pointer",
        background: checked ? "#f0f9ff" : "transparent",
        transition: "background 0.1s",
      }}
    >
      <Checkbox checked={checked} onChange={onChange} disabled={disabled} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 12.5, fontWeight: 500,
          color: checked ? "#0c4a6e" : "#1e293b",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }} title={kpi.title}>
          {kpi.title || "—"}
        </div>
        {allTags.length > 0 && (
          <div style={{ display: "flex", gap: 4, marginTop: 4, flexWrap: "wrap" }}>
            {allTags.slice(0, 4).map((t, i) => (
              <span key={i} style={{
                fontSize: 10, color: "#475569",
                background: "#f1f5f9", border: "1px solid #e2e8f0",
                borderRadius: 4, padding: "1px 6px",
              }}>
                {t.value}
              </span>
            ))}
            {allTags.length > 4 && (
              <span style={{ fontSize: 10, color: "#94a3b8" }}>+{allTags.length - 4}</span>
            )}
          </div>
        )}
      </div>
      {checked && (
        <span style={{
          fontSize: 9, fontWeight: 700, color: "#0369a1",
          background: "#e0f2fe", borderRadius: 4, padding: "2px 6px",
        }}>
          SELECTED
        </span>
      )}
    </div>
  );
}

// ─── Trie helpers ─────────────────────────────────────────────────────────────
function buildTrie(questions) {
  const root = { children: new Map(), kpiIds: new Set() };
  for (const q of questions) {
    const combos = parseTags(q.tags);
    for (const combo of combos)
      if (combo && combo.length) insertCombo(root, combo, q.questionId);
  }
  return root;
}

function insertCombo(node, combo, qId) {
  node.kpiIds.add(qId);
  if (!combo.length) return;
  const tag = combo[0];
  if (!tag || !tag.key || !tag.value) return;
  if (!node.children.has(tag.key)) node.children.set(tag.key, new Map());
  const km = node.children.get(tag.key);
  if (!km.has(tag.value)) km.set(tag.value, { children: new Map(), kpiIds: new Set() });
  insertCombo(km.get(tag.value), combo.slice(1), qId);
}

function traverseTrieMulti(trie, steps) {
  let nodes = [trie];
  for (const step of steps) {
    if (!step?.key || !step?.value) continue;
    const next = [];
    for (const node of nodes) {
      const km = node.children?.get(step.key);
      if (!km) continue;
      const c = km.get(step.value);
      if (c) next.push(c);
    }
    if (!next.length) return [];
    nodes = next;
  }
  return nodes;
}

function getAvailableKeysMulti(nodes, usedKeys) {
  const kvm = new Map();
  for (const node of nodes) {
    if (!node?.children) continue;
    for (const [key, vm] of node.children.entries()) {
      if (usedKeys.has(key)) continue;
      if (!kvm.has(key)) kvm.set(key, new Set());
      for (const v of vm.keys()) kvm.get(key).add(v);
    }
  }
  return Array.from(kvm.entries()).map(([key, values]) => ({
    key, values: Array.from(values).sort(),
  }));
}

// ─── SelectedDrillNode ────────────────────────────────────────────────────────
function SelectedDrillNode({ keyName, questions, pendingIds, onToggle, savedIds, disabled }) {
  const [isOpen, setIsOpen] = useState(false);
  const [internalPath, setInternalPath] = useState([]);

  const relevantQuestions = useMemo(() => questions.filter(q => {
    if (!pendingIds.has(q.questionId)) return false;
    return parseTags(q.tags).some(c => c.some(t => t?.key === keyName));
  }), [questions, pendingIds, keyName]);

  const allQuestionsForKey = useMemo(() =>
    questions.filter(q => parseTags(q.tags).some(c => c.some(t => t?.key === keyName))),
    [questions, keyName]);

  const trie = useMemo(() => buildTrie(relevantQuestions), [relevantQuestions]);

  const currentKpis = useMemo(() => {
    if (internalPath.length === 0) return relevantQuestions;
    const nodes = traverseTrieMulti(trie, internalPath);
    const validIds = new Set();
    nodes.forEach(n => n?.kpiIds?.forEach(id => validIds.add(id)));
    return relevantQuestions.filter(q => validIds.has(q.questionId));
  }, [relevantQuestions, internalPath, trie]);

  const topValues = useMemo(() => {
    const vals = new Set();
    const km = trie.children?.get(keyName);
    if (km) for (const v of km.keys()) vals.add(v);
    return Array.from(vals).sort();
  }, [trie, keyName]);

  const valueStats = useMemo(() => {
    const stats = new Map();
    for (const val of topValues) {
      const total = allQuestionsForKey.filter(q =>
        parseTags(q.tags).some(c => c.some(t => t?.key === keyName && t?.value === val))
      ).length;
      const selected = allQuestionsForKey.filter(q =>
        pendingIds.has(q.questionId) &&
        parseTags(q.tags).some(c => c.some(t => t?.key === keyName && t?.value === val))
      ).length;
      stats.set(val, { total, selected });
    }
    return stats;
  }, [topValues, allQuestionsForKey, pendingIds, keyName]);

  const nextAvailableKeyData = useMemo(() => {
    if (internalPath.length === 0) return [];
    const nodes = traverseTrieMulti(trie, internalPath);
    const used = new Set([keyName, ...internalPath.map(p => p.key)]);
    return getAvailableKeysMulti(nodes, used);
  }, [trie, internalPath, keyName]);

  const handleDropdownChange = (key, value, index) => {
    setInternalPath(prev => {
      const newPath = [...prev.slice(0, index)];
      if (value) newPath.push({ key, value });
      return newPath;
    });
  };

  const hasUnsaved = !disabled && relevantQuestions.some(q => !savedIds.has(q.questionId));
  const selectedCount = relevantQuestions.length;
  const totalForKey = allQuestionsForKey.length;

  const pathLevelOptions = useMemo(() => {
    const options = [];
    for (let i = 1; i < internalPath.length; i++) {
      const pathUpTo = internalPath.slice(0, i);
      const nodes = traverseTrieMulti(trie, pathUpTo);
      const vals = new Set();
      for (const node of nodes) {
        const km = node.children?.get(internalPath[i].key);
        if (km) for (const v of km.keys()) vals.add(v);
      }
      options.push({ key: internalPath[i].key, values: Array.from(vals).sort() });
    }
    return options;
  }, [trie, internalPath]);

  return (
    <div style={{ borderBottom: "1px solid #f1f5f9" }}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "13px 20px", cursor: "pointer",
          background: isOpen ? "#f8fafc" : "#fff",
          transition: "background 0.15s", userSelect: "none",
        }}
      >
        <div style={{
          width: 22, height: 22, borderRadius: 6,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: isOpen ? "#3f88a5" : "#e2e8f0",
          transition: "all 0.2s", flexShrink: 0,
        }}>
          <span style={{
            fontSize: 8, color: isOpen ? "#fff" : "#64748b",
            transform: isOpen ? "rotate(90deg)" : "none",
            display: "block", transition: "transform 0.2s",
          }}>▶</span>
        </div>
        <span style={{
          fontSize: 11, fontWeight: 700, color: "#3f88a5",
          textTransform: "uppercase", letterSpacing: "0.8px",
        }}>
          {formatTagKey(keyName)}
        </span>
        {hasUnsaved && (
          <span style={{
            fontSize: 9, fontWeight: 700, color: "#d97706",
            background: "#fef3c7", border: "1px solid #fcd34d",
            borderRadius: 4, padding: "1px 6px",
          }}>UNSAVED</span>
        )}
        <div style={{ flex: 1 }} />
        <span style={{
          fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 20,
          background: selectedCount === totalForKey ? "#e0f2fe" : "#fef3c7",
          color: selectedCount === totalForKey ? "#0369a1" : "#d97706",
          border: `1px solid ${selectedCount === totalForKey ? "#bae6fd" : "#fcd34d"}`,
          whiteSpace: "nowrap",
        }}>
          {selectedCount}/{totalForKey}
        </span>
      </div>

      {isOpen && (
        <div style={{ background: "#f8fafc", borderTop: "1px solid #f1f5f9" }}>
          {topValues.length > 0 && (
            <div style={{ padding: "12px 20px", borderBottom: "1px solid #f1f5f9" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", marginBottom: 8, minHeight: 24 }}>
                {internalPath.length > 0 && (
                  <button
                    onClick={() => setInternalPath([])}
                    style={{
                      fontSize: 10, color: "#ef4444",
                      border: "1px solid #fecaca", background: "#fff5f5",
                      borderRadius: 5, padding: "2px 8px",
                      cursor: "pointer", fontWeight: 600,
                    }}
                  >✕ Reset</button>
                )}
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 180px", minWidth: 0 }}>
                  <label style={{
                    fontSize: 10, fontWeight: 700, color: "#64748b",
                    display: "block", marginBottom: 5,
                    textTransform: "uppercase", letterSpacing: "0.4px",
                  }}>
                    {formatTagKey(keyName)}
                  </label>
                  <select
                    style={{
                      width: "100%", padding: "7px 10px", borderRadius: 7,
                      border: `1.5px solid ${internalPath[0]?.key === keyName ? "#93c5fd" : "#e2e8f0"}`,
                      fontSize: 12, outline: "none", background: "#fff",
                      color: "#374151", cursor: "pointer",
                    }}
                    value={internalPath[0]?.key === keyName ? internalPath[0].value : ""}
                    onChange={e => handleDropdownChange(keyName, e.target.value, 0)}
                  >
                    <option value="">All {formatTagKey(keyName)}s</option>
                    {topValues.map(v => {
                      const s = valueStats.get(v) || { selected: 0, total: 0 };
                      return (
                        <option key={v} value={v}>
                          {v} ({s.selected}/{s.total})
                        </option>
                      );
                    })}
                  </select>
                </div>

                {internalPath.length > 1 && pathLevelOptions.map((levelOpts, idx) => (
                  <div key={internalPath[idx + 1].key} style={{ flex: "1 1 180px", minWidth: 0 }}>
                    <label style={{
                      fontSize: 10, fontWeight: 700, color: "#64748b",
                      display: "block", marginBottom: 5,
                      textTransform: "uppercase", letterSpacing: "0.4px",
                    }}>
                      {formatTagKey(internalPath[idx + 1].key)}
                    </label>
                    <select
                      style={{
                        width: "100%", padding: "7px 10px", borderRadius: 7,
                        border: "1.5px solid #93c5fd", fontSize: 12,
                        outline: "none", background: "#fff",
                        color: "#374151", cursor: "pointer",
                      }}
                      value={internalPath[idx + 1].value}
                      onChange={e => handleDropdownChange(internalPath[idx + 1].key, e.target.value, idx + 1)}
                    >
                      <option value="">All {formatTagKey(internalPath[idx + 1].key)}s</option>
                      {levelOpts.values.map(v => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </select>
                  </div>
                ))}

                {internalPath.length > 0 && nextAvailableKeyData.length > 0 && (
                  <div style={{ flex: "1 1 180px", minWidth: 0 }}>
                    <label style={{
                      fontSize: 10, fontWeight: 700, color: "#3f88a5",
                      display: "block", marginBottom: 5,
                      textTransform: "uppercase", letterSpacing: "0.4px",
                    }}>
                      {formatTagKey(nextAvailableKeyData[0].key)}
                    </label>
                    <select
                      style={{
                        width: "100%", padding: "7px 10px", borderRadius: 7,
                        border: "1.5px solid #bae6fd", fontSize: 12,
                        outline: "none", background: "#f0f9ff",
                        color: "#0369a1", cursor: "pointer",
                      }}
                      value=""
                      onChange={e => {
                        const [k, v] = e.target.value.split(":::");
                        if (k && v) handleDropdownChange(k, v, internalPath.length);
                      }}
                    >
                      <option value="">Select {formatTagKey(nextAvailableKeyData[0].key)}…</option>
                      {nextAvailableKeyData.map(group => (
                        <optgroup key={group.key} label={formatTagKey(group.key).toUpperCase()}>
                          {group.values.map(val => (
                            <option key={`${group.key}-${val}`} value={`${group.key}:::${val}`}>
                              {val}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {internalPath.length > 0 && (
                <div style={{
                  marginTop: 10, display: "flex", alignItems: "center", gap: 6,
                  padding: "8px 12px", background: "#f0f9ff",
                  borderRadius: 8, border: "1px solid #bae6fd",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 4, flex: 1, flexWrap: "wrap" }}>
                    {internalPath.map((step, i) => (
                      <span key={step.key} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        {i > 0 && <span style={{ color: "#bae6fd", fontSize: 13 }}>›</span>}
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#1d4ed8" }}>
                          {step.value}
                        </span>
                      </span>
                    ))}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                    <span style={{ fontSize: 10, color: "#64748b" }}>selected</span>
                    <span style={{
                      fontSize: 11, fontWeight: 800, padding: "2px 9px",
                      borderRadius: 10, background: "#0369a1", color: "#fff",
                    }}>
                      {currentKpis.filter(q => pendingIds.has(q.questionId)).length}
                    </span>
                    <span style={{ fontSize: 10, color: "#94a3b8" }}>/</span>
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: "2px 9px",
                      borderRadius: 10, background: "#e0f2fe",
                      color: "#0369a1", border: "1px solid #bae6fd",
                    }}>
                      {currentKpis.length}
                    </span>
                    <span style={{ fontSize: 10, color: "#64748b" }}>available</span>
                  </div>
                </div>
              )}
            </div>
          )}
          <div style={{
            margin: "0 20px 16px", background: "#fff",
            border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden",
          }}>
            {currentKpis.length === 0 ? (
              <div style={{ padding: "24px", textAlign: "center", color: "#94a3b8", fontSize: 12 }}>
                No KPIs match this filter.
              </div>
            ) : (
              <>
                <div style={{
                  padding: "8px 14px", background: "#f8fafc",
                  borderBottom: "1px solid #f1f5f9",
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700, color: "#64748b",
                    textTransform: "uppercase", letterSpacing: "0.4px",
                  }}>KPI Title</span>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {!disabled && currentKpis.some(q => pendingIds.has(q.questionId)) && (
                      <button
                        onClick={() => currentKpis.forEach(q => onToggle(q.questionId, false))}
                        style={{
                          fontSize: 10, fontWeight: 600, color: "#dc2626",
                          background: "#fff5f5", border: "1px solid #fecaca",
                          borderRadius: 5, padding: "2px 8px", cursor: "pointer",
                        }}
                      >
                        Deselect all
                      </button>
                    )}
                    {!disabled && currentKpis.some(q => !pendingIds.has(q.questionId)) && (
                      <button
                        onClick={() => currentKpis.forEach(q => onToggle(q.questionId, true))}
                        style={{
                          fontSize: 10, fontWeight: 600, color: "#0369a1",
                          background: "#e0f2fe", border: "1px solid #bae6fd",
                          borderRadius: 5, padding: "2px 8px", cursor: "pointer",
                        }}
                      >
                        Select all
                      </button>
                    )}
                    <span style={{
                      fontSize: 10, fontWeight: 700, color: "#64748b",
                      textTransform: "uppercase", letterSpacing: "0.4px",
                    }}>
                      {currentKpis.length} item{currentKpis.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                </div>
                {currentKpis.map((q, idx) => (
                  <div
                    key={q.questionId}
                    style={{
                      display: "flex", alignItems: "center", gap: 12,
                      padding: "10px 14px",
                      borderBottom: idx === currentKpis.length - 1 ? "none" : "1px solid #f8fafc",
                      background: "#fff",
                    }}
                  >
                    <Checkbox
                      checked={pendingIds.has(q.questionId)}
                      onChange={c => !disabled && onToggle(q.questionId, c)}
                      disabled={disabled}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span style={{
                        fontSize: 12.5, color: "#1e293b", fontWeight: 450,
                        overflow: "hidden", textOverflow: "ellipsis",
                        whiteSpace: "nowrap", display: "block",
                      }} title={q.title}>
                        {q.title}
                      </span>
                    </div>
                    {!savedIds.has(q.questionId) && !disabled && (
                      <span style={{
                        fontSize: 9, fontWeight: 700, color: "#d97706",
                        background: "#fef3c7", border: "1px solid #fcd34d",
                        borderRadius: 4, padding: "1px 6px", flexShrink: 0,
                      }}>UNSAVED</span>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── SelectedKpisAccordion ────────────────────────────────────────────────────
function SelectedKpisAccordion({ questions, savedIds, pendingIds, onToggle, disabled, onBulkDeselect }) {
  const [selectedSearch, setSelectedSearch] = useState("");

  const topLevelKeys = useMemo(() => {
    const keys = new Set();
    questions.filter(q => pendingIds.has(q.questionId)).forEach(q => {
      parseTags(q.tags).forEach(c => { if (c[0]?.key) keys.add(c[0].key); });
    });
    return Array.from(keys).sort();
  }, [questions, pendingIds]);

  const filteredKeys = useMemo(() => {
    if (!selectedSearch.trim()) return topLevelKeys;
    const q = selectedSearch.toLowerCase();
    return topLevelKeys.filter(key => {
      if (formatTagKey(key).toLowerCase().includes(q)) return true;
      return questions.some(kpi =>
        pendingIds.has(kpi.questionId) &&
        parseTags(kpi.tags).some(c => c.some(t => t?.key === key)) &&
        kpi.title?.toLowerCase().includes(q)
      );
    });
  }, [topLevelKeys, selectedSearch, questions, pendingIds]);

  if (pendingIds.size === 0) {
    return (
      <div style={{ padding: "60px 20px", textAlign: "center" }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>○</div>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#64748b", marginBottom: 6 }}>
          No KPIs selected
        </div>
        <div style={{ fontSize: 12, color: "#94a3b8" }}>
          Switch to the All KPIs tab to add KPIs.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{
        padding: "10px 20px", background: "#f0f9ff",
        borderBottom: "1px solid #bae6fd",
        display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
      }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: "#0369a1" }}>{pendingIds.size}</span>
        <span style={{ fontSize: 13, color: "#64748b" }}>selected out of</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: "#0369a1" }}>{questions.length}</span>
        <div style={{ flex: 1 }} />
        {!disabled && (
          <button
            onClick={() => onBulkDeselect(Array.from(pendingIds))}
            style={{
              fontSize: 11, fontWeight: 600, color: "#dc2626",
              background: "#fff5f5", border: "1px solid #fecaca",
              borderRadius: 7, padding: "4px 12px", cursor: "pointer",
              display: "flex", alignItems: "center", gap: 4,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "#fee2e2"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#fff5f5"; }}
          >
            ✕ Deselect all
          </button>
        )}
      </div>

      <div style={{ padding: "10px 20px", borderBottom: "1px solid #f1f5f9" }}>
        <div style={{ position: "relative" }}>
          <span style={{
            position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)",
            fontSize: 12, color: "#94a3b8", pointerEvents: "none",
          }}>⌕</span>
          <input
            value={selectedSearch}
            onChange={e => setSelectedSearch(e.target.value)}
            placeholder="Search selected KPIs by tag or title…"
            style={{
              width: "100%", fontSize: 12, padding: "7px 28px 7px 28px",
              border: "1.5px solid #e2e8f0", borderRadius: 8,
              background: "#fff", color: "#1e293b", outline: "none",
            }}
            onFocus={e => e.target.style.borderColor = "#93c5fd"}
            onBlur={e => e.target.style.borderColor = "#e2e8f0"}
          />
          {selectedSearch && (
            <span
              onClick={() => setSelectedSearch("")}
              style={{
                position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                fontSize: 14, color: "#94a3b8", cursor: "pointer",
              }}
            >×</span>
          )}
        </div>
      </div>

      {filteredKeys.length === 0 ? (
        <div style={{ padding: "24px", textAlign: "center", fontSize: 12, color: "#94a3b8" }}>
          No results for "{selectedSearch}"
        </div>
      ) : (
        filteredKeys.map(key => (
          <SelectedDrillNode
            key={key}
            keyName={key}
            questions={questions}
            pendingIds={pendingIds}
            onToggle={onToggle}
            savedIds={savedIds}
            disabled={disabled}
          />
        ))
      )}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function EmissionKPISettings() {
  const [canUpdate] = usePermission("active-kpis.update", "KPIS");
  const [allQuestions, setAllQuestions] = useState([]);
  const [savedIds, setSavedIds] = useState(new Set());
  const [pendingIds, setPendingIds] = useState(new Set());
  const [isEnabled, setIsEnabled] = useState(true);
  const [filterSteps, setFilterSteps] = useState([]);
  const [search, setSearch] = useState("");
  const [kpiView, setKpiView] = useState("list");
  const [loading, setLoading] = useState(true);
  // existingTags = the raw tags object from the API response, shown as-is in SelectedTagsDisplay
  const [existingTags, setExistingTags] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [toast, setToast] = useState(null);

  const fyData = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("financialYears")) ?? []; } catch { return []; }
  }, []);
  const latestFY = useMemo(() => getLatestFY(fyData), [fyData]);
  const [selectedFY, setSelectedFY] = useState(() => latestFY);
  const isViewOnly = selectedFY?.id !== latestFY?.id;
  const isDisableEdit = isViewOnly || !canUpdate;

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const prevLeafKey = useRef(null);

  const fetchAll = useCallback(async () => {
    if (!selectedFY?.id) return;
    setLoading(true);
    try {
      const [settingsRes] = await Promise.all([
        apiCall(`${config.POSTLOGIN_API_URL_COMPANY}emissionKpiSettings`, {}, { fyid: selectedFY.id }, "GET"),
      ]);
      const userId = JSON.parse(localStorage.getItem("user_temp_id"));
      const fwRes = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}getFramework`, {},
        { type: "ALL", userId }, "GET"
      );
      const frameworks = fwRes.data?.data || [];
      const qRes = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}getReportingQuestionnew`, {},
        { financialYearId: selectedFY.id, frameworkIds: frameworks.map(f => f.id), fromSettings: true, question_type_filter: JSON.stringify(['quantitative']) },
        "GET"
      );
      const questions = qRes.data?.data || [];
      setAllQuestions(questions);

      const validQuestionIds = new Set(questions.map(q => q.questionId));
      const raw = settingsRes.data;
      setIsEnabled(raw?.is_enabled ?? true);
      const savedK = Array.isArray(raw?.kpiIds) ? raw.kpiIds.map(Number) : [];
      const ids = raw?.id
        ? new Set(savedK.filter(id => validQuestionIds.has(id)))
        : new Set(questions.filter(q => q.kpi_type?.includes("EMISSION")).map(q => q.questionId));

      setSavedIds(ids);
      setPendingIds(new Set(ids));

      // Parse and store the API tags exactly as returned — this is what we display
      let parsedTags = {};
      if (raw?.tags) {
        try {
          parsedTags = typeof raw.tags === "string" ? JSON.parse(raw.tags) : raw.tags;
        } catch {
          parsedTags = {};
        }
      }
      setExistingTags(parsedTags || {});
    } catch {
      showToast("Error loading data", "error");
    } finally {
      setLoading(false);
    }
  }, [selectedFY?.id]);

  useEffect(() => {
    setFilterSteps([]);
    setSearch("");
    setKpiView("list");
    prevLeafKey.current = null;
    fetchAll();
  }, [fetchAll]);

  const activeSteps = filterSteps.filter(s => s.values?.size);
  const nextEligible = useMemo(
    () => getNextEligibleFilters(allQuestions, filterSteps, savedIds),
    [allQuestions, filterSteps, savedIds]
  );
  const isLeaf = activeSteps.length > 0 && nextEligible.length === 0;

  const handleSelectFiltered = useCallback((steps, change) => {
    if (isDisableEdit) return;
    const active = steps.filter(s => s.values?.size);
    if (!active.length) {
      if (change?.removed?.length) {
        setPendingIds(prev => {
          const next = new Set(prev);
          change.removed.forEach(({ key, value }) => {
            const path = [{ key, values: new Set([value]) }];
            const matching = allQuestions.filter(q => questionMatchesSteps(q, path));
            matching.forEach(q => {
              if (!savedIds.has(q.questionId)) {
                next.delete(q.questionId);
              }
            });
          });
          return next;
        });
      }
      return;
    }

    setPendingIds(prev => {
      const next = new Set(prev);

      // 1. Select all KPIs matching the full active filter steps.
      const fullMatching = allQuestions.filter(q => questionMatchesSteps(q, active));
      fullMatching.forEach(q => next.add(q.questionId));

      // 2. Filter out sibling categories under the parent prefix.
      const L = active.length;
      if (L > 1) {
        const prefixSteps = active.slice(0, L - 1);
        const parentMatching = allQuestions.filter(q => questionMatchesSteps(q, prefixSteps));
        parentMatching.forEach(q => {
          if (!questionMatchesSteps(q, active)) {
            if (!savedIds.has(q.questionId)) {
              next.delete(q.questionId);
            }
          }
        });
      }

      // 3. Process explicitly removed tags
      if (change?.removed?.length) {
        change.removed.forEach(({ key, value }) => {
          const stepIdx = steps.findIndex(s => s.key === key);
          const stepsPrefix = stepIdx >= 0 ? steps.slice(0, stepIdx) : steps;
          const path = [...stepsPrefix, { key, values: new Set([value]) }];
          const matching = allQuestions.filter(q => questionMatchesSteps(q, path));
          matching.forEach(q => {
            if (!questionMatchesSteps(q, steps)) {
              if (!savedIds.has(q.questionId)) {
                next.delete(q.questionId);
              }
            }
          });
        });
      }

      return next;
    });
  }, [allQuestions, isDisableEdit, savedIds]);

  const leafQuestions = useMemo(() => {
    if (!isLeaf) return [];
    return allQuestions.filter(q => questionMatchesSteps(q, filterSteps));
  }, [isLeaf, allQuestions, filterSteps]);

  useEffect(() => {
    if (isDisableEdit || !isLeaf || leafQuestions.length === 0) return;
    const leafKey = filterSteps.map(s => `${s.key}:${[...s.values].join(",")}`).join("|");
    if (prevLeafKey.current === leafKey) return;
    prevLeafKey.current = leafKey;
    setPendingIds(prev => {
      const next = new Set(prev);
      leafQuestions.forEach(q => next.add(q.questionId));
      return next;
    });
  }, [isLeaf, leafQuestions, filterSteps, isDisableEdit]);

  const toggleKpi = useCallback((id, checked) => {
    if (isDisableEdit) return;
    setPendingIds(p => { const n = new Set(p); checked ? n.add(id) : n.delete(id); return n; });
  }, [isDisableEdit]);

  const handleBulkDeselect = useCallback((ids) => {
    if (isDisableEdit) return;
    setPendingIds(prev => {
      const next = new Set(prev);
      ids.forEach(id => next.delete(id));
      return next;
    });
  }, [isDisableEdit]);

  // currentTags:
  //  1. merge existingTags with any new activeSteps filter selections
  //  2. prune any tag value whose KPIs have all been deselected from pendingIds
  // This is what gets POSTed on save and shown in SelectedTagsDisplay.
  const currentTags = useMemo(() => {
    const merged = mergeTagsPayload(existingTags, buildTagsPayload(activeSteps));
    return pruneTagsByPendingIds(merged, allQuestions, pendingIds);
  }, [activeSteps, existingTags, allQuestions, pendingIds]);

  const handleSave = async () => {
    if (isDisableEdit) return;
    setSaving(true);
    const kpiIds = Array.from(pendingIds);
    const { isSuccess } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}emissionKpiSettings`, {},
      { kpiIds, tags: currentTags, is_enabled: isEnabled, fyid: selectedFY?.id },
      "POST"
    );
    if (isSuccess) {
      setSavedIds(new Set(pendingIds));
      // After save, the tags we just saved become the new "API tags"
      setExistingTags(currentTags);
      setSaved(true);
      setFilterSteps([]);
      setSearch("");
      prevLeafKey.current = null;
      showToast("Preferences saved!");
      setTimeout(() => setSaved(false), 2000);
    }
    setSaving(false);
  };

  const hasPendingChanges = useMemo(() => {
    if (isDisableEdit) return false;
    if (pendingIds.size !== savedIds.size) return true;
    for (const id of pendingIds) if (!savedIds.has(id)) return true;
    return false;
  }, [pendingIds, savedIds, isDisableEdit]);

  const hasActiveFilter = activeSteps.length > 0;
  const filteredQuestions = useMemo(() => {
    if (!activeSteps.length) return allQuestions;
    return allQuestions.filter(q => questionMatchesSteps(q, filterSteps));
  }, [activeSteps, allQuestions, filterSteps]);
  const filteredSelectedCount = filteredQuestions.filter(q => pendingIds.has(q.questionId)).length;

  if (loading) {
    return (
      <div style={{
        padding: 100, textAlign: "center",
        display: "flex", flexDirection: "column", alignItems: "center", gap: 16,
      }}>
        <Spinner size={32} color="#3f88a5" />
        <span style={{ fontSize: 13, color: "#94a3b8" }}>Loading KPI preferences…</span>
      </div>
    );
  }

  return (
    <div style={{
      fontFamily: "'Segoe UI', system-ui, sans-serif",
      background: "#f1f5f9", minHeight: "100vh",
    }}>
      <style>{`
        @keyframes ekpi-spin { to { transform: rotate(360deg) } }
        @keyframes ekpi-toast-in { from { opacity: 0; transform: translateY(10px) } to { opacity: 1; transform: translateY(0) } }
        * { box-sizing: border-box; }
        select:focus { outline: none; }
        button:focus-visible { outline: 2px solid #3f88a5; outline-offset: 2px; }
      `}</style>

      {toast && (
        <div style={{
          position: "fixed", bottom: 24, right: 24,
          background: toast.type === "error" ? "#dc2626" : "#16a34a",
          color: "#fff", padding: "11px 18px", borderRadius: 10,
          zIndex: 1000, fontSize: 13, fontWeight: 600,
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          animation: "ekpi-toast-in 0.25s ease",
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <span>{toast.type === "error" ? "✕" : "✓"}</span>
          {toast.msg}
        </div>
      )}

      {/* Top Bar */}
      <div style={{
        background: "#fff", padding: "0 28px", height: 56,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        borderBottom: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 8, height: 28, borderRadius: 4,
            background: "linear-gradient(to bottom, #3f88a5, #2563eb)",
          }} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
              Emission KPI Preferences
            </div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>
              {pendingIds.size} KPI{pendingIds.size !== 1 ? "s" : ""} selected
              {hasPendingChanges && (
                <span style={{ color: "#d97706", fontWeight: 600 }}> · unsaved changes</span>
              )}
              {(isViewOnly || !canUpdate) && (
                <span style={{ color: "#b45309", fontWeight: 600 }}> · view only</span>
              )}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {fyData.length > 0 && (
            <SimpleDropdown
              label="Financial Year"
              value={selectedFY?.id ?? ""}
              onChange={val => {
                const found = fyData.find(f => String(f.id) === String(val));
                if (found) setSelectedFY(found);
              }}
              options={fyData.map(fy => ({
                value: fy.id,
                label: `${fy.financial_year_value}${fy.id === latestFY?.id ? " (Current)" : ""}`,
              }))}
              isAlert={selectedFY?.id !== latestFY?.id}
            />
          )}
          <button
            onClick={handleSave}
            disabled={saving || !hasPendingChanges || isDisableEdit}
            style={{
              background: hasPendingChanges && !isDisableEdit ? "#3f88a5" : "#e2e8f0",
              color: hasPendingChanges && !isDisableEdit ? "#fff" : "#94a3b8",
              border: "none", padding: "9px 22px", borderRadius: 8,
              cursor: hasPendingChanges && !isDisableEdit ? "pointer" : "default",
              fontSize: 13, fontWeight: 600,
              display: "flex", alignItems: "center", gap: 8,
              transition: "all 0.2s",
              boxShadow: hasPendingChanges && !isDisableEdit
                ? "0 2px 8px rgba(63,136,165,0.35)" : "none",
            }}
          >
            {saving
              ? <><Spinner size={12} color="#fff" /> Saving…</>
              : saved ? "✓ Saved!" : "Save Preferences"
            }
          </button>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: "24px", maxWidth: 1040, margin: "0 auto" }}>
        {isViewOnly && <ViewOnlyBanner fyValue={selectedFY?.financial_year_value} />}

        {/* Tabs */}
        <div style={{
          display: "flex", gap: 0, background: "#e2e8f0",
          padding: 3, borderRadius: 10, width: "fit-content", marginBottom: 20,
        }}>
          {[
            { key: "list", label: "All KPIs", count: allQuestions.length },
            { key: "selected", label: "Selected", count: pendingIds.size },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setKpiView(tab.key)}
              style={{
                padding: "7px 20px", border: "none", borderRadius: 8,
                background: kpiView === tab.key ? "#fff" : "transparent",
                cursor: "pointer", fontSize: 12, fontWeight: 600,
                color: kpiView === tab.key ? "#0f172a" : "#64748b",
                display: "flex", alignItems: "center", gap: 7,
                boxShadow: kpiView === tab.key ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                transition: "all 0.15s",
              }}
            >
              {tab.label}
              <span style={{
                fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 10,
                background: kpiView === tab.key
                  ? (tab.key === "selected" ? "#dbeafe" : "#f1f5f9")
                  : "#d1d5db",
                color: kpiView === tab.key
                  ? (tab.key === "selected" ? "#1d4ed8" : "#475569")
                  : "#94a3b8",
              }}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* ── All KPIs Tab ──────────────────────────────────────────────── */}
        {kpiView === "list" && (
          <>
            <div style={{
              background: "#fff", padding: "16px", borderRadius: 12,
              border: "1px solid #e2e8f0", marginBottom: 12,
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            }}>
              <div style={{ position: "relative", marginBottom: 12 }}>
                <span style={{
                  position: "absolute", left: 11, top: "50%",
                  transform: "translateY(-50%)", fontSize: 14, color: "#94a3b8",
                }}>⌕</span>
                <input
                  value={search}
                  onChange={e => !isViewOnly && setSearch(e.target.value)}
                  readOnly={isViewOnly}
                  placeholder="Search by title or module…"
                  style={{
                    width: "100%", padding: "9px 12px 9px 32px",
                    border: "1.5px solid #e2e8f0", borderRadius: 9,
                    fontSize: 13, outline: "none", color: "#1e293b",
                    background: isViewOnly ? "#f8fafc" : "#fff",
                    cursor: isViewOnly ? "default" : "text",
                  }}
                  onFocus={e => { if (!isViewOnly) e.target.style.borderColor = "#93c5fd"; }}
                  onBlur={e => e.target.style.borderColor = "#e2e8f0"}
                />
              </div>
              <TagFilterBar
                allQuestions={allQuestions}
                filterSteps={filterSteps}
                onFilterStepsChange={setFilterSteps}
                savedIds={savedIds}
                pendingIds={pendingIds}
                disabled={isViewOnly}
                onSelectFiltered={handleSelectFiltered}
                disableAllSelected={true}
              />
            </div>

            {/* Matching filter summary */}
            {hasActiveFilter && (
              <div style={{
                background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0",
                boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                padding: "16px 20px", marginBottom: 12,
                display: "flex", alignItems: "center",
                justifyContent: "space-between", flexWrap: "wrap", gap: 12,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  <div>
                    <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 2 }}>
                      Matching filter
                    </div>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                      <span style={{ fontSize: 26, fontWeight: 800, color: "#0369a1" }}>
                        {filteredSelectedCount}
                      </span>
                      <span style={{ fontSize: 13, color: "#64748b" }}>
                        / {filteredQuestions.length} KPIs selected
                      </span>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    {filteredQuestions.length > 0 &&
                      filteredSelectedCount < filteredQuestions.length &&
                      !isDisableEdit && (
                        <button
                          onClick={() => handleSelectFiltered(filterSteps)}
                          style={{
                            padding: "6px 14px", borderRadius: 7,
                            border: "1.5px solid #3f88a5",
                            background: "#f0f9ff", color: "#0369a1",
                            fontSize: 11, fontWeight: 600,
                            cursor: "pointer", whiteSpace: "nowrap",
                          }}
                        >
                          + Select all {filteredQuestions.length}
                        </button>
                      )}
                  </div>
                </div>
                <div style={{
                  padding: "7px 14px", borderRadius: 8,
                  background: "#f8fafc", border: "1px solid #e2e8f0",
                  fontSize: 11, color: "#64748b",
                }}>
                  {activeSteps.map((s, i) => (
                    <span key={s.key}>
                      {i > 0 && <span style={{ margin: "0 4px", color: "#cbd5e1" }}>›</span>}
                      <span style={{ fontWeight: 700, color: "#1d4ed8" }}>
                        {formatTagKey(s.key)}:{" "}
                      </span>
                      <span style={{ color: "#374151" }}>
                        {Array.from(s.values).join(", ")}
                      </span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Saved tags panel — shows only API-returned tags */}
            {Object.keys(currentTags).length > 0 && (
              <SelectedTagsDisplay existingTags={currentTags} />
            )}

            {/* KPI list / leaf / idle */}
            {isLeaf ? (
              <>
                <div style={{
                  display: "flex", justifyContent: "space-between",
                  alignItems: "center", marginBottom: 8, padding: "0 2px",
                }}>
                  <span style={{ fontSize: 11, color: "#94a3b8" }}>
                    {leafQuestions.length} KPI{leafQuestions.length !== 1 ? "s" : ""}
                    {isDisableEdit ? " — view only" : " — auto-selected, uncheck to exclude"}
                  </span>
                  <span style={{ fontSize: 11, color: "#0369a1", fontWeight: 600 }}>
                    {leafQuestions.filter(q => pendingIds.has(q.questionId)).length} / {leafQuestions.length} selected
                  </span>
                </div>
                <div style={{
                  background: "#fff", borderRadius: 12,
                  border: "1px solid #e2e8f0", overflow: "hidden",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                }}>
                  {leafQuestions.length === 0 ? (
                    <div style={{ padding: "60px 20px", textAlign: "center" }}>
                      <div style={{ fontSize: 13, color: "#94a3b8" }}>
                        No KPIs match this filter combination.
                      </div>
                    </div>
                  ) : (
                    <div style={{ maxHeight: 580, overflowY: "auto" }}>
                      {leafQuestions.map((q, idx) => (
                        <KpiListRow
                          key={q.questionId}
                          kpi={q}
                          checked={pendingIds.has(q.questionId)}
                          onChange={c => toggleKpi(q.questionId, c)}
                          isLast={idx === leafQuestions.length - 1}
                          disabled={isDisableEdit}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : !hasActiveFilter ? (
              <div style={{
                background: "#fff", borderRadius: 12,
                border: "1px dashed #e2e8f0",
                boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                padding: "48px 20px", textAlign: "center",
              }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#94a3b8", marginBottom: 6 }}>
                  Select a filter above to get started
                </div>
                <div style={{ fontSize: 12, color: "#cbd5e1" }}>
                  KPIs appear once all filter levels are chosen
                </div>
              </div>
            ) : null}
          </>
        )}

        {/* ── Selected Tab ──────────────────────────────────────────────── */}
        {kpiView === "selected" && (
          <div style={{
            background: "#fff", borderRadius: 12,
            border: "1px solid #e2e8f0", overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          }}>
            <SelectedKpisAccordion
              questions={allQuestions}
              savedIds={savedIds}
              pendingIds={pendingIds}
              onToggle={toggleKpi}
              disabled={isDisableEdit}
              onBulkDeselect={handleBulkDeselect}
            />
          </div>
        )}
      </div>
    </div>
  );
}